import http from "@services/httpService"
import { LNotification } from "@lib/abpUtility"
import { notifySuccess } from "@lib/helper"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import type {
  ApproverGroupOption,
  IApproverGroupDto,
  IApproverGroupMembersInputDto,
  ICreateOrUpdateApproverGroupDto,
  IPagedApproverGroupRequestDto,
} from "@services/approverGroup/approverGroupDto"

/**
 * Feature-0182 — single HTTP layer for the ApproverGroup domain (R17/R19).
 * All approver-group requests go through this service; callers receive plain DTOs.
 */
class ApproverGroupService {
  public async getAll(
    params: IPagedApproverGroupRequestDto
  ): Promise<PagedResultDto<IApproverGroupDto>> {
    const res = await http.get("api/services/app/ApproverGroup/GetAll", {
      params,
    })
    return res.data.result
  }

  public async get(id: number): Promise<IApproverGroupDto> {
    const res = await http.get("api/services/app/ApproverGroup/Get", {
      params: { id },
    })
    return res.data.result
  }

  public async create(
    body: ICreateOrUpdateApproverGroupDto
  ): Promise<IApproverGroupDto> {
    const res = await http.post("api/services/app/ApproverGroup/Create", body)
    notifySuccess(LNotification("SUCCESS"), LNotification("SAVING_SUCCESSFULLY"))
    return res.data.result
  }

  public async update(
    body: ICreateOrUpdateApproverGroupDto
  ): Promise<IApproverGroupDto> {
    const res = await http.put("api/services/app/ApproverGroup/Update", body)
    notifySuccess(LNotification("SUCCESS"), LNotification("SAVING_SUCCESSFULLY"))
    return res.data.result
  }

  public async deactivate(id: number): Promise<void> {
    await http.post("api/services/app/ApproverGroup/Deactivate", { id })
    notifySuccess(LNotification("SUCCESS"), LNotification("UPDATE_SUCCESSFULLY"))
  }

  public async addMembers(
    body: IApproverGroupMembersInputDto
  ): Promise<IApproverGroupDto> {
    const res = await http.post(
      "api/services/app/ApproverGroup/AddMembers",
      body
    )
    notifySuccess(LNotification("SUCCESS"), LNotification("SAVING_SUCCESSFULLY"))
    return res.data.result
  }

  public async removeMembers(
    body: IApproverGroupMembersInputDto
  ): Promise<IApproverGroupDto> {
    const res = await http.post(
      "api/services/app/ApproverGroup/RemoveMembers",
      body
    )
    notifySuccess(LNotification("SUCCESS"), LNotification("SAVING_SUCCESSFULLY"))
    return res.data.result
  }

  public async delete(id: number): Promise<void> {
    await http.delete("api/services/app/ApproverGroup/Delete", {
      params: { id },
    })
    notifySuccess(LNotification("SUCCESS"), LNotification("DELETE_SUCCESSFULLY"))
  }

  /**
   * Active-group options for the per-step picker (R19.1): active groups of the current tenant with a
   * member-count for the badge. Membership is not snapshotted — the count is display-only.
   */
  public async getActiveGroupOptions(): Promise<ApproverGroupOption[]> {
    const res = await this.getAll({ isActive: true, maxResultCount: 1000, skipCount: 0 })
    return (res.items ?? []).map((group) => ({
      value: group.id,
      label: group.name,
      groupCode: group.groupCode,
      memberCount: (group.members ?? []).length,
    }))
  }
}

export default new ApproverGroupService()
